// MarketingAudiencias — /admin/marketing/audiencias
// Panel del Analista de Audiencias. Lee posts_ig_metricas + cuenta_ig_metricas_diarias.
// Rediseño moderno 2026-09-01: look v2.0 flotante (Navy/Teal/Sky/Paper, Inter + Manrope),
// KPIs con sparkline real de followers, cards con profundidad suave, responsive por container query.
// Namespace rz- para no chocar estilos globales.

import { useEffect, useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import AdminLayout from '../../components/AdminLayout'
import { supabase } from '../../lib/supabase'

const PILAR_LABEL = {
  costo_invisible: 'Costo invisible',
  diccionario: 'Diccionario',
  diario_barrio: 'Diario de barrio',
  expectativa: 'Expectativa',
  lanzamiento: 'Lanzamiento',
  retencion: 'Retención',
  porteros_referidos: 'Porteros',
  otro: 'Otro',
}

export default function MarketingAudiencias() {
  const [loading, setLoading] = useState(true)
  const [cuentaDias, setCuentaDias] = useState([])
  const [piezasMetricas, setPiezasMetricas] = useState([])
  const [ultimaCorrida, setUltimaCorrida] = useState(null)

  useEffect(() => { cargar() }, [])

  async function cargar() {
    setLoading(true)
    try {
      const { data: cuenta } = await supabase
        .from('cuenta_ig_metricas_diarias')
        .select('fecha, followers_count, follows_count, reach_dia, impressions_dia, profile_views, audience_gender_age, audience_top_cities, audience_top_countries, created_at')
        .order('fecha', { ascending: false })
        .limit(7)
      setCuentaDias(cuenta || [])

      const { data: piezas } = await supabase
        .from('posts_ig_metricas')
        .select(`
          id, post_id, pulled_at, edad_post_hs,
          reach, impressions, saves, likes, comments, shares,
          engagement_rate,
          posts_ig:post_id ( id, pilar, formato, ig_post_id, publicado_en, brief )
        `)
        .order('pulled_at', { ascending: false })
        .limit(50)
      setPiezasMetricas(piezas || [])

      const ultimaCuenta = cuenta?.[0]?.created_at
      const ultimaPieza = piezas?.[0]?.pulled_at
      const candidatas = [ultimaCuenta, ultimaPieza].filter(Boolean).sort().reverse()
      setUltimaCorrida(candidatas[0] || null)
    } catch (e) {
      console.error('audiencias cargar:', e)
    } finally {
      setLoading(false)
    }
  }

  const piezasUltimas = useMemo(() => {
    const map = new Map()
    for (const m of piezasMetricas) {
      if (!m.post_id) continue
      if (!map.has(m.post_id)) map.set(m.post_id, m)
    }
    return Array.from(map.values())
  }, [piezasMetricas])

  const cuentaHoy = cuentaDias[0]
  const cuentaSiete = cuentaDias[cuentaDias.length - 1]
  const followersHoy = cuentaHoy?.followers_count ?? null
  const followersHaceUnaSemana = cuentaSiete?.followers_count ?? null
  const followersDelta = (followersHoy !== null && followersHaceUnaSemana !== null)
    ? followersHoy - followersHaceUnaSemana : null
  const serieFollowers = useMemo(
    () => [...cuentaDias].reverse().map(d => d?.followers_count).filter(v => typeof v === 'number'),
    [cuentaDias]
  )

  const reach7d = piezasUltimas.reduce((s, p) => s + (Number(p.reach) || 0), 0)
  const piezasMedidas = piezasUltimas.length
  const saveRatePromedio = piezasUltimas.length > 0
    ? piezasUltimas.reduce((s, p) => s + (Number(p.engagement_rate) || 0), 0) / piezasUltimas.length
    : null
  const maxEng = piezasUltimas.reduce((s, p) => Math.max(s, Number(p.engagement_rate) || 0), 0)

  const pilarStats = useMemo(() => {
    const acc = {}
    for (const m of piezasUltimas) {
      const pilar = m.posts_ig?.pilar || 'otro'
      if (!acc[pilar]) acc[pilar] = { pilar, piezas: 0, engagement: 0 }
      acc[pilar].piezas += 1
      acc[pilar].engagement += Number(m.engagement_rate) || 0
    }
    return Object.values(acc)
      .map(p => ({ ...p, engagement_avg: p.engagement / p.piezas }))
      .sort((a, b) => b.engagement_avg - a.engagement_avg)
  }, [piezasUltimas])
  const maxRankEng = pilarStats.reduce((s, p) => Math.max(s, p.engagement_avg || 0), 0)

  const THRESHOLD_DEMOGRAFICA = 100
  const llegoThreshold = (followersHoy ?? 0) >= THRESHOLD_DEMOGRAFICA
  const porcentajeThreshold = Math.min(100, ((followersHoy ?? 0) / THRESHOLD_DEMOGRAFICA) * 100)

  return (
    <AdminLayout>
      <div className="rz">

        <div className="rz-head">
          <div className="rz-head-t">
            <div className="rz-eyebrow">Comunicación · Cómo rindió lo publicado</div>
            <h1 className="rz-h1">Resultados</h1>
            <p className="rz-sub">
              <IcoDot />
              {ultimaCorrida
                ? `Última corrida: ${new Date(ultimaCorrida).toLocaleString('es-UY', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}`
                : 'Sin corridas aún · el cron corre cada noche 22:00 Mvd'}
            </p>
          </div>
          <div className="rz-head-actions">
            <Link to="/admin/comunicacion" className="rz-btn"><IcoBack /><span className="rz-btn-t">Panel</span></Link>
          </div>
        </div>

        {!loading && !llegoThreshold && (
          <div className="rz-alert">
            <div className="rz-alert-t">Meta libera la audiencia demográfica a los 100 followers</div>
            <div className="rz-alert-b">
              Estamos en <strong>{followersHoy ?? '—'} followers</strong>. Faltan para los <strong>{THRESHOLD_DEMOGRAFICA}</strong> que
              Meta necesita para devolver edad/género y top ciudades/países. Mientras tanto sí trackeamos reach, saves y engagement por pieza.
            </div>
            <div className="rz-prog"><span className="rz-prog-i" style={{ width: `${porcentajeThreshold}%` }} /></div>
            <div className="rz-prog-l">{followersHoy ?? 0} / {THRESHOLD_DEMOGRAFICA}</div>
          </div>
        )}

        <Link to="/admin/analytics/trafico" className="rz-tile">
          <span className="rz-tile-ic"><IcoActivity /></span>
          <span className="rz-tile-t">
            <strong>Tráfico web · Analytics en vivo</strong>
            <span>Cloudflare · visitantes UY · top páginas · errores · por día</span>
          </span>
          <span className="rz-arrow"><IcoArrow /></span>
        </Link>

        <section className="rz-kpis" aria-label="Indicadores">
          <div className="rz-kpi rz-hero">
            <div className="rz-kpi-main">
              <div className="rz-kpi-l">Followers <span className="rz-hdl">@limpackuy</span></div>
              <div className="rz-kpi-v">{followersHoy ?? '—'}</div>
              <div className="rz-kpi-s">seguidores de Instagram</div>
            </div>
            <div className="rz-kpi-aux">
              {followersDelta !== null && (
                <div className={`rz-delta${followersDelta >= 0 ? ' up' : ''}`}>{followersDelta >= 0 ? <IcoUp /> : null}{followersDelta >= 0 ? `+${followersDelta}` : followersDelta} · 7d</div>
              )}
              <Sparkline values={serieFollowers} />
            </div>
          </div>
          <div className="rz-kpi">
            <div className="rz-kpi-main">
              <div className="rz-kpi-l">Piezas medidas</div>
              <div className="rz-kpi-v">{piezasMedidas}</div>
              <div className="rz-kpi-s">{piezasMedidas === 0 ? 'esperando publicación' : 'con métricas de IG'}</div>
            </div>
          </div>
          <div className="rz-kpi">
            <div className="rz-kpi-main">
              <div className="rz-kpi-l">Reach últimos 7d</div>
              <div className="rz-kpi-v">{reach7d || '—'}</div>
              <div className="rz-kpi-s">suma de las piezas</div>
            </div>
          </div>
          <div className="rz-kpi">
            <div className="rz-kpi-main">
              <div className="rz-kpi-l">Save rate promedio</div>
              <div className="rz-kpi-v">{saveRatePromedio ? <>{(saveRatePromedio * 100).toFixed(1)}<small>%</small></> : '—'}</div>
              <div className="rz-kpi-s">indicador de intención</div>
            </div>
          </div>
        </section>

        <section className="rz-card rz-sec-perf">
          <header className="rz-card-h">
            <div>
              <h2>Performance por pieza</h2>
              <p>{piezasMedidas} pieza{piezasMedidas !== 1 ? 's' : ''} con métricas · más reciente primero</p>
            </div>
          </header>
          {loading && <div className="rz-empty-mini">Cargando…</div>}
          {!loading && piezasMedidas === 0 && (
            <div className="rz-card-b"><div className="rz-empty"><strong>Sin piezas publicadas aún.</strong><p>Cuando se publique la primera pieza, el agente la mide a las 22:00 Mvd y aparece acá con su reach, saves, likes y engagement.</p></div></div>
          )}
          {!loading && piezasMedidas > 0 && (
            <div className="rz-tblwrap">
              <table>
                <thead>
                  <tr>
                    <th>Pieza</th><th>Pilar</th><th className="n">Edad</th><th className="n">Reach</th>
                    <th className="n">Saves</th><th className="n">Likes</th><th className="n">Coment.</th><th className="n">Engagement</th>
                  </tr>
                </thead>
                <tbody>
                  {piezasUltimas.map(m => {
                    const titulo = (m.posts_ig?.brief || '').split('\n')[0]?.slice(0, 60) || 'Sin título'
                    const pilar = m.posts_ig?.pilar
                    const esLanz = (pilar || '').includes('lanz')
                    const eng = Number(m.engagement_rate) || 0
                    const engW = maxEng > 0 ? Math.round((eng / maxEng) * 100) : 0
                    return (
                      <tr key={m.id}>
                        <td className="piece">
                          {titulo}
                          {m.posts_ig?.ig_post_id && (
                            <a href={`https://www.instagram.com/p/${m.posts_ig.ig_post_id}/`} target="_blank" rel="noopener noreferrer" className="rz-ext" title="Ver en Instagram">↗</a>
                          )}
                        </td>
                        <td className="pilar">{pilar ? <span className={`rz-chip${esLanz ? ' teal' : ''}`}>{PILAR_LABEL[pilar] || pilar}</span> : <span className="rz-dash">—</span>}</td>
                        <td className="n dim edad">{m.edad_post_hs ? `${m.edad_post_hs}h` : '—'}</td>
                        <td className="n m" data-l="Reach">{m.reach ?? '—'}</td>
                        <td className="n m" data-l="Saves">{m.saves ?? '—'}</td>
                        <td className="n m" data-l="Likes">{m.likes ?? '—'}</td>
                        <td className="n m" data-l="Coment.">{m.comments ?? '—'}</td>
                        <td className="n e" data-l="Engagement"><div className="rz-eng"><b>{m.engagement_rate ? `${(eng * 100).toFixed(1)}%` : '—'}</b><span className="rz-mini"><i style={{ width: `${engW}%` }} /></span></div></td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="rz-card rz-sec-rank">
          <header className="rz-card-h"><div><h2>Ranking por pilar</h2><p>Qué temas funcionan mejor</p></div></header>
          {!loading && pilarStats.length === 0 && (
            <div className="rz-card-b"><div className="rz-empty-mini">Necesitamos al menos 2 piezas medidas para comparar pilares.</div></div>
          )}
          {pilarStats.length > 0 && (
            <div className="rz-rank">
              {pilarStats.map((p, i) => {
                const w = maxRankEng > 0 ? Math.round((p.engagement_avg / maxRankEng) * 100) : 0
                return (
                  <div key={p.pilar} className="rz-rk">
                    <div className={`rz-rk-n${i === 0 ? ' first' : ' rest'}`}>{i + 1}</div>
                    <div><div className="rz-rk-t">{PILAR_LABEL[p.pilar] || p.pilar}</div><div className="rz-rk-s">{p.piezas} pieza{p.piezas !== 1 ? 's' : ''}</div></div>
                    <div className="rz-rk-v">{(p.engagement_avg * 100).toFixed(1)}%</div>
                    <div className="rz-rk-bar"><i style={{ width: `${w}%` }} /></div>
                  </div>
                )
              })}
            </div>
          )}
        </section>

        {llegoThreshold && cuentaHoy?.audience_gender_age && (
          <section className="rz-card rz-sec-aud">
            <header className="rz-card-h">
              <div><h2>Audiencia demográfica</h2><p>Quiénes te siguen en Instagram</p></div>
              <span className="rz-meta"><IcoIg />Snapshot {fmtFechaCorta(cuentaHoy.fecha)}</span>
            </header>
            <div className="rz-card-b">
              <Demografia genderAge={cuentaHoy.audience_gender_age} cities={cuentaHoy.audience_top_cities} countries={cuentaHoy.audience_top_countries} />
            </div>
          </section>
        )}

        <p className="rz-foot">
          {llegoThreshold
            ? <>Con la cuenta arriba de 100 followers, Meta ya devuelve la audiencia demográfica (edad/género, top ciudades y países). Meta discontinuó “horarios pico online”, por eso no aparece. El panel se actualiza solo cada noche 22:00 Mvd.</>
            : <>Cuando @limpackuy llegue a 100 followers, Meta libera la audiencia demográfica. El panel se actualiza solo cada noche 22:00 Mvd.</>}
        </p>
      </div>
      <Estilos />
    </AdminLayout>
  )
}

/* ── Sparkline (serie real de followers) ── */
function Sparkline({ values }) {
  const vals = (values || []).filter(v => typeof v === 'number')
  if (vals.length < 2) return null
  const w = 184, h = 54, p = 5
  const min = Math.min(...vals), max = Math.max(...vals), range = (max - min) || 1
  const stepX = (w - p * 2) / (vals.length - 1)
  const pts = vals.map((v, i) => [+(p + i * stepX).toFixed(1), +(h - p - ((v - min) / range) * (h - p * 2)).toFixed(1)])
  const line = pts.map(pt => pt.join(',')).join(' ')
  const area = `${p},${h - p} ${line} ${w - p},${h - p}`
  const last = pts[pts.length - 1]
  return (
    <svg className="rz-spark" viewBox={`0 0 ${w} ${h}`} aria-hidden="true">
      <defs><linearGradient id="rzspark" x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor="var(--rz-teal)" stopOpacity="0.26" /><stop offset="1" stopColor="var(--rz-teal)" stopOpacity="0" /></linearGradient></defs>
      <polygon points={area} fill="url(#rzspark)" />
      <polyline points={line} fill="none" stroke="var(--rz-teal)" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={last[0]} cy={last[1]} r="5" fill="var(--rz-teal)" opacity="0.18" />
      <circle cx={last[0]} cy={last[1]} r="2.8" fill="var(--rz-teal)" stroke="var(--rz-surface)" strokeWidth="1.5" />
    </svg>
  )
}

/* ── Demografía ── */
function fmtFechaCorta(f) {
  const s = String(f || '').slice(0, 10)
  const [y, m, d] = s.split('-').map(Number)
  if (!y || !m || !d) return s
  return new Date(y, m - 1, d).toLocaleDateString('es-UY', { day: '2-digit', month: 'short' })
}
const GEN_LABEL = { F: 'Mujeres', M: 'Hombres', U: 'Sin dato' }
const PAIS_LABEL = { UY: 'Uruguay', BR: 'Brasil', MX: 'México', AR: 'Argentina', ES: 'España', US: 'EE.UU.' }
const AGE_ORDER = ['13-17', '18-24', '25-34', '35-44', '45-54', '55-64', '65+']

function Demografia({ genderAge, cities, countries }) {
  const byAge = {}, byGender = {}
  for (const r of (genderAge || [])) {
    const dv = r.dimension_values || []
    const age = dv[0], gen = dv[1]
    const v = Number(r.value) || 0
    if (age) byAge[age] = (byAge[age] || 0) + v
    if (gen) byGender[gen] = (byGender[gen] || 0) + v
  }
  const edad = AGE_ORDER.filter(a => byAge[a] != null).map(a => ({ label: a.replace('-', '–'), value: byAge[a] }))
  const genero = ['F', 'M', 'U'].filter(g => byGender[g] != null).map(g => ({ label: GEN_LABEL[g] || g, value: byGender[g], nd: g === 'U' }))
  const ciudades = (cities || []).map(r => ({ label: (r.dimension_values?.[0] || '').split(',')[0], value: Number(r.value) || 0 })).sort((a, b) => b.value - a.value).slice(0, 8)
  const paises = (countries || []).map(r => ({ label: PAIS_LABEL[r.dimension_values?.[0]] || r.dimension_values?.[0] || '—', value: Number(r.value) || 0 })).sort((a, b) => b.value - a.value)

  const totGM = (byGender.F || 0) + (byGender.M || 0)
  const generoDom = (byGender.F || 0) >= (byGender.M || 0) ? 'mujeres' : 'hombres'
  const generoPct = totGM ? Math.round((Math.max(byGender.F || 0, byGender.M || 0) / totGM) * 100) : 0
  const edadTop = [...edad].sort((a, b) => b.value - a.value).slice(0, 2)
  const bounds = edadTop.flatMap(e => e.label.replace('+', '').split('–').map(Number)).filter(n => !isNaN(n))
  const rangoEdad = bounds.length ? `${Math.min(...bounds)} a ${Math.max(...bounds)}` : '—'
  const ciudadTop = ciudades[0]?.label || '—'

  return (
    <>
      {edad.length > 0 && (
        <p className="rz-plain">Tu público es sobre todo <b>{generoDom}{generoPct ? ` (${generoPct}%)` : ''}</b> de <b>{rangoEdad} años</b>, de <b>{ciudadTop}</b>.</p>
      )}
      <div className="rz-demo">
        <DemoBlock titulo="Por género" rows={genero} />
        <DemoBlock titulo="Por edad" rows={edad} />
        <DemoBlock titulo="Top ciudades" rows={ciudades} />
        <DemoBlock titulo="Por país" rows={paises} />
      </div>
      <p className="rz-demo-foot">Fuente: Instagram (Meta) · % sobre seguidores con datos · el género se calcula sin contar “Sin dato”.</p>
    </>
  )
}
function DemoBlock({ titulo, rows }) {
  const max = Math.max(1, ...rows.map(r => r.value))
  const total = rows.reduce((s, r) => s + r.value, 0)
  return (
    <div className="rz-blk">
      <h3>{titulo}</h3>
      {rows.length === 0 && <div className="rz-demo-empty">—</div>}
      {rows.map(r => (
        <div key={r.label} className="rz-row">
          <span className="rz-lb" title={r.label}>{r.label}</span>
          <span className="rz-bar"><i className={r.nd ? 'nd' : ''} style={{ width: `${(r.value / max) * 100}%` }} /></span>
          <span className="rz-val"><b>{r.value}</b><span>{r.nd ? '—' : (total ? `${Math.round((r.value / total) * 100)}%` : '')}</span></span>
        </div>
      ))}
    </div>
  )
}

/* ── Íconos ── */
const sp = { fill: 'none', stroke: 'currentColor', strokeWidth: 1.8, strokeLinecap: 'round', strokeLinejoin: 'round' }
function IcoActivity() { return <svg viewBox="0 0 24 24" {...sp} style={{ width: 18, height: 18 }}><path d="M22 12h-4l-3 9L9 3l-3 9H2" /></svg> }
function IcoArrow() { return <svg viewBox="0 0 24 24" {...sp} style={{ width: 16, height: 16 }}><path d="M5 12h14M13 6l6 6-6 6" /></svg> }
function IcoUp() { return <svg viewBox="0 0 24 24" {...sp} strokeWidth="2.2" style={{ width: 13, height: 13 }}><path d="M3 17l6-6 4 4 8-8M17 7h4v4" /></svg> }
function IcoBack() { return <svg viewBox="0 0 24 24" {...sp} style={{ width: 15, height: 15 }}><path d="M19 12H5M11 18l-6-6 6-6" /></svg> }
function IcoIg() { return <svg viewBox="0 0 24 24" {...sp} style={{ width: 14, height: 14 }}><rect x="3" y="3" width="18" height="18" rx="5" /><circle cx="12" cy="12" r="4" /><circle cx="17.5" cy="6.5" r="0.9" fill="currentColor" stroke="none" /></svg> }
function IcoDot() { return <svg viewBox="0 0 24 24" {...sp} style={{ width: 14, height: 14 }}><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg> }

function Estilos() {
  return (
    <style>{`
      .rz{
        --rz-navy:#1A3461; --rz-teal:#00A8B5; --rz-sky:#5BBDD8;
        --rz-surface:#fff; --rz-surface-2:#F7F9FC; --rz-surface-3:#EDF1F6;
        --rz-ink:#0F1F3D; --rz-ink-2:#3B4A66; --rz-ink-3:#66748F; --rz-ink-4:#98A3B8;
        --rz-hair:rgba(26,52,97,.10); --rz-hair-2:rgba(26,52,97,.06); --rz-track:#E4EAF2;
        --rz-teal-ink:#0A7C86; --rz-teal-soft:rgba(0,168,181,.10); --rz-sky-soft:rgba(91,189,216,.16);
        --rz-navy-soft:rgba(26,52,97,.07); --rz-ok:#1A8F56; --rz-ok-soft:rgba(31,157,94,.11);
        --rz-display:Manrope, Inter, system-ui, sans-serif;
        --r-lg:18px; --r-md:14px;
        --sh-card:0 1px 1px rgba(26,52,97,.03), 0 2px 6px -1px rgba(26,52,97,.05), 0 16px 36px -16px rgba(26,52,97,.12);
        --sh-hover:0 1px 1px rgba(26,52,97,.03), 0 6px 14px -4px rgba(26,52,97,.10), 0 26px 46px -18px rgba(26,52,97,.18);
        container-type:inline-size;
        max-width:1160px; margin:0 auto; display:grid; grid-template-columns:repeat(12,1fr); gap:20px;
        color:var(--rz-ink); font-family:Inter, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
      }
      .rz > *{ grid-column:1 / -1; min-width:0 }
      .rz .n{ font-variant-numeric:tabular-nums }

      .rz-head{ display:flex; justify-content:space-between; align-items:flex-end; gap:20px; padding:6px 0 2px }
      .rz-head-t{ min-width:0 }
      .rz-eyebrow{ display:inline-flex; align-items:center; gap:9px; font-size:11.5px; font-weight:600; letter-spacing:.09em; text-transform:uppercase; color:var(--rz-teal-ink) }
      .rz-eyebrow::before{ content:""; width:18px; height:2px; border-radius:2px; background:var(--rz-teal) }
      .rz-h1{ margin:8px 0 0; font-family:var(--rz-display); font-size:32px; font-weight:800; letter-spacing:-.03em; line-height:1.05; color:var(--rz-ink) }
      .rz-sub{ margin:9px 0 0; font-size:13px; color:var(--rz-ink-3); display:flex; align-items:center; gap:6px }
      .rz-sub svg{ color:var(--rz-ink-4) }
      .rz-head-actions{ display:flex; align-items:center; gap:10px; flex:none }
      .rz-btn{ height:38px; padding:0 14px 0 12px; display:inline-flex; align-items:center; gap:7px; border-radius:12px; background:var(--rz-surface); color:var(--rz-ink-2); font-size:13px; font-weight:500; text-decoration:none; box-shadow:inset 0 0 0 1px var(--rz-hair), var(--sh-card); transition:box-shadow .15s, color .15s }
      .rz-btn:hover{ color:var(--rz-ink); box-shadow:inset 0 0 0 1px rgba(0,168,181,.4), var(--sh-hover) }

      .rz-alert{ background:var(--rz-surface); border-radius:var(--r-lg); border-left:3px solid var(--rz-teal); padding:16px 18px; box-shadow:inset 0 0 0 1px var(--rz-hair-2), var(--sh-card) }
      .rz-alert-t{ font-size:14px; font-weight:700; color:var(--rz-ink) }
      .rz-alert-b{ font-size:12.5px; color:var(--rz-ink-2); margin-top:5px; line-height:1.5 }
      .rz-prog{ margin-top:11px; height:8px; background:var(--rz-track); border-radius:5px; overflow:hidden }
      .rz-prog-i{ display:block; height:100%; background:linear-gradient(90deg,var(--rz-teal),var(--rz-sky)); border-radius:5px }
      .rz-prog-l{ margin-top:4px; font-size:11px; font-weight:600; color:var(--rz-ink-3); text-align:right; font-variant-numeric:tabular-nums }

      .rz-tile{ display:flex; align-items:center; gap:12px; padding:8px 16px 8px 8px; border-radius:var(--r-md); background:var(--rz-surface); box-shadow:inset 0 0 0 1px var(--rz-hair-2), var(--sh-card); text-decoration:none; color:inherit; transition:transform .15s, box-shadow .15s }
      .rz-tile:hover{ transform:translateY(-1px); box-shadow:inset 0 0 0 1px rgba(0,168,181,.4), var(--sh-hover) }
      .rz-tile-ic{ width:42px; height:42px; border-radius:12px; background:linear-gradient(135deg,var(--rz-teal-soft),var(--rz-sky-soft)); color:var(--rz-teal-ink); display:grid; place-items:center; flex:none }
      .rz-tile-t{ display:flex; flex-direction:column; gap:1px; min-width:0 }
      .rz-tile-t strong{ font-size:13.5px; font-weight:600; display:inline-flex; align-items:center; gap:7px }
      .rz-tile-t strong::after{ content:""; width:6px; height:6px; border-radius:50%; background:var(--rz-ok); box-shadow:0 0 0 3px var(--rz-ok-soft) }
      .rz-tile-t span{ font-size:12px; color:var(--rz-ink-3); white-space:nowrap; overflow:hidden; text-overflow:ellipsis }
      .rz-arrow{ margin-left:auto; color:var(--rz-ink-4); display:grid; place-items:center; transition:transform .15s, color .15s }
      .rz-tile:hover .rz-arrow{ transform:translateX(3px); color:var(--rz-teal-ink) }

      .rz-kpis{ display:grid; grid-template-columns:1.5fr 1fr 1fr 1fr; gap:16px }
      .rz-kpi{ position:relative; background:var(--rz-surface); border-radius:var(--r-lg); padding:20px 22px 18px; min-height:150px; box-shadow:inset 0 0 0 1px var(--rz-hair-2), var(--sh-card); display:flex; flex-direction:column; overflow:hidden; transition:box-shadow .2s, transform .2s }
      .rz-kpi:hover{ box-shadow:inset 0 0 0 1px var(--rz-hair), var(--sh-hover); transform:translateY(-1px) }
      .rz-kpi-main{ display:flex; flex-direction:column; gap:8px; flex:1; min-width:0 }
      .rz-kpi-l{ font-size:12.5px; font-weight:600; color:var(--rz-ink-3); display:flex; gap:6px; align-items:center; flex-wrap:wrap }
      .rz-hdl{ font-weight:500; color:var(--rz-ink-4) }
      .rz-kpi-v{ font-family:var(--rz-display); font-size:44px; font-weight:800; letter-spacing:-.04em; line-height:1; color:var(--rz-ink); font-variant-numeric:tabular-nums }
      .rz-kpi-v small{ font-size:22px; font-weight:700; color:var(--rz-ink-3); margin-left:2px }
      .rz-kpi-s{ font-size:12.5px; color:var(--rz-ink-3); margin-top:auto }
      .rz-hero{ display:grid; grid-template-columns:1fr auto; align-items:end; column-gap:12px }
      .rz-hero::after{ content:""; position:absolute; right:-18%; bottom:-45%; width:60%; height:100%; border-radius:50%; background:radial-gradient(closest-side,rgba(0,168,181,.10),transparent); pointer-events:none }
      .rz-kpi-aux{ align-self:end; width:min(100%,188px); display:flex; flex-direction:column; align-items:flex-end; gap:8px }
      .rz-delta{ display:inline-flex; align-items:center; gap:4px; padding:4px 9px 4px 6px; border-radius:999px; background:var(--rz-ok-soft); color:var(--rz-ok); font-size:12px; font-weight:600; font-variant-numeric:tabular-nums }
      .rz-spark{ width:100%; height:auto; display:block; overflow:visible }

      .rz-card{ background:var(--rz-surface); border-radius:var(--r-lg); box-shadow:inset 0 0 0 1px var(--rz-hair-2), var(--sh-card); overflow:hidden }
      .rz-card-h{ display:flex; justify-content:space-between; align-items:center; gap:16px; padding:20px 24px 12px }
      .rz-card-h h2{ margin:0; font-family:var(--rz-display); font-size:17px; font-weight:700; letter-spacing:-.015em; color:var(--rz-ink) }
      .rz-card-h p{ margin:3px 0 0; font-size:12.5px; color:var(--rz-ink-3) }
      .rz-meta{ font-size:12px; color:var(--rz-ink-3); display:inline-flex; align-items:center; gap:6px; white-space:nowrap; padding:6px 10px; border-radius:999px; background:var(--rz-surface-3) }
      .rz-card-b{ padding:8px 24px 24px }
      .rz-sec-perf{ grid-column:1 / -1 }
      .rz-sec-rank{ grid-column:span 4 }
      .rz-sec-aud{ grid-column:span 8 }

      .rz-empty{ background:var(--rz-surface-2); border:1px dashed var(--rz-hair); border-radius:12px; padding:20px; text-align:center; color:var(--rz-ink-3) }
      .rz-empty strong{ display:block; margin-bottom:6px; color:var(--rz-ink); font-size:14px }
      .rz-empty p{ margin:0 auto; font-size:12.5px; line-height:1.5; max-width:520px }
      .rz-empty-mini{ margin:14px 24px 22px; background:var(--rz-surface-2); border:1px dashed var(--rz-hair); border-radius:10px; padding:14px; text-align:center; color:var(--rz-ink-3); font-size:12.5px }

      .rz-tblwrap{ overflow-x:auto; padding:0 10px 10px }
      .rz table{ width:100%; border-collapse:separate; border-spacing:0; font-size:13.5px; color:inherit }
      .rz th{ text-align:left; font-size:11px; font-weight:600; letter-spacing:.07em; text-transform:uppercase; color:var(--rz-ink-4); padding:8px 14px 10px; border-bottom:1px solid var(--rz-hair); white-space:nowrap }
      .rz td{ padding:13px 14px; border-bottom:1px solid var(--rz-hair-2); vertical-align:middle; white-space:nowrap }
      .rz tbody tr:last-child td{ border-bottom:0 }
      .rz tbody tr td:first-child{ border-radius:10px 0 0 10px }
      .rz tbody tr td:last-child{ border-radius:0 10px 10px 0 }
      .rz tbody tr:hover td{ background:var(--rz-surface-2) }
      .rz th.n, .rz td.n{ text-align:right; font-variant-numeric:tabular-nums }
      .rz td.piece{ font-weight:500; color:var(--rz-ink); max-width:340px; overflow:hidden; text-overflow:ellipsis }
      .rz td.dim{ color:var(--rz-ink-3) }
      .rz-ext{ color:var(--rz-teal-ink); text-decoration:none; margin-left:6px; font-size:13px }
      .rz-chip{ display:inline-flex; align-items:center; padding:3px 9px; border-radius:999px; font-size:11.5px; font-weight:600; background:var(--rz-navy-soft); color:var(--rz-ink-2) }
      .rz-chip.teal{ background:var(--rz-teal-soft); color:var(--rz-teal-ink) }
      .rz-dash{ color:var(--rz-ink-4) }
      .rz-eng{ display:flex; justify-content:flex-end; align-items:center; gap:10px }
      .rz-eng b{ font-weight:700; min-width:50px; text-align:right; font-variant-numeric:tabular-nums }
      .rz-mini{ width:64px; height:6px; background:var(--rz-track); border-radius:3px; overflow:hidden; flex:none }
      .rz-mini i{ display:block; height:100%; border-radius:3px; background:linear-gradient(90deg,var(--rz-teal),var(--rz-sky)) }

      .rz-rank{ display:grid; gap:12px; padding:6px 20px 20px }
      .rz-rk{ display:grid; grid-template-columns:auto 1fr auto; align-items:center; gap:12px 14px; padding:16px 16px 18px; border-radius:14px; background:var(--rz-surface-2); box-shadow:inset 0 0 0 1px var(--rz-hair-2) }
      .rz-rk-n{ width:30px; height:30px; border-radius:10px; display:grid; place-items:center; font-family:var(--rz-display); font-weight:800; font-size:13px }
      .rz-rk-n.first{ background:var(--rz-teal); color:#fff; box-shadow:0 8px 14px -8px rgba(0,168,181,.9) }
      .rz-rk-n.rest{ background:var(--rz-navy-soft); color:var(--rz-ink-2) }
      .rz-rk-t{ font-weight:600; font-size:14px }
      .rz-rk-s{ font-size:12.5px; color:var(--rz-ink-3); margin-top:2px }
      .rz-rk-v{ font-family:var(--rz-display); font-size:23px; font-weight:800; letter-spacing:-.03em; font-variant-numeric:tabular-nums; color:var(--rz-ink) }
      .rz-rk-bar{ grid-column:1 / -1; height:6px; background:var(--rz-track); border-radius:3px; overflow:hidden }
      .rz-rk-bar i{ display:block; height:100%; border-radius:3px; background:linear-gradient(90deg,var(--rz-teal),var(--rz-sky)) }

      .rz-plain{ margin:4px 0 22px; font-size:17px; line-height:1.5; color:var(--rz-ink); letter-spacing:-.005em }
      .rz-plain b{ font-weight:700; color:var(--rz-navy); background:linear-gradient(transparent 60%, var(--rz-sky-soft) 60%); border-radius:3px; padding:0 3px }
      .rz-demo{ display:grid; grid-template-columns:1fr 1fr; gap:24px 36px }
      .rz-blk h3{ margin:0 0 10px; font-size:11px; font-weight:600; letter-spacing:.08em; text-transform:uppercase; color:var(--rz-ink-4) }
      .rz-row{ display:grid; grid-template-columns:106px 1fr 74px; gap:12px; align-items:center; height:28px }
      .rz-lb{ font-size:13px; color:var(--rz-ink-2); white-space:nowrap; overflow:hidden; text-overflow:ellipsis }
      .rz-bar{ height:8px; background:var(--rz-track); border-radius:4px; overflow:hidden }
      .rz-bar i{ display:block; height:100%; min-width:4px; border-radius:4px; background:linear-gradient(90deg,var(--rz-teal),var(--rz-sky)) }
      .rz-bar i.nd{ background:repeating-linear-gradient(135deg,var(--rz-ink-4) 0 2px,transparent 2px 6px); opacity:.55 }
      .rz-val{ display:flex; justify-content:flex-end; align-items:baseline; gap:8px; font-size:13px; font-variant-numeric:tabular-nums }
      .rz-val b{ font-weight:700; color:var(--rz-ink) }
      .rz-val span{ color:var(--rz-ink-4); min-width:32px; text-align:right }
      .rz-demo-empty{ color:var(--rz-ink-4); font-size:12px }
      .rz-demo-foot{ margin:22px 0 0; font-size:12px; color:var(--rz-ink-4) }

      .rz-foot{ background:var(--rz-surface-2); border-radius:14px; box-shadow:inset 0 0 0 1px var(--rz-hair-2); padding:14px 18px; font-size:12px; color:var(--rz-ink-3); line-height:1.6; margin:0 }

      @container (max-width:1000px){
        .rz-kpis{ grid-template-columns:1fr 1fr }
        .rz-sec-rank, .rz-sec-aud{ grid-column:1 / -1 }
        .rz-head{ flex-direction:column; align-items:flex-start }
      }
      @container (max-width:640px){
        .rz{ gap:12px }
        .rz-h1{ font-size:28px }
        .rz-kpis{ grid-template-columns:1fr; gap:10px }
        .rz-kpi, .rz-hero{ display:grid; grid-template-columns:1fr auto; align-items:center; column-gap:14px; padding:14px 16px; min-height:0 }
        .rz-kpi-main{ gap:6px }
        .rz-kpi-v{ font-size:32px }
        .rz-kpi-v small{ font-size:18px }
        .rz-kpi-aux{ width:124px }
        .rz-hero::after{ display:none }
        .rz-card-h{ padding:16px 16px 10px }
        .rz-card-b{ padding:6px 16px 18px }
        .rz-rank{ padding:4px 16px 16px }
        .rz-plain{ font-size:15.5px; margin-bottom:18px }
        .rz-demo{ grid-template-columns:1fr; gap:20px }
        .rz-row{ grid-template-columns:88px 1fr 64px; gap:10px }
        .rz-val span{ min-width:28px }
        .rz-sec-aud{ order:1 } .rz-sec-perf{ order:2 } .rz-sec-rank{ order:3 }
        .rz-tblwrap{ overflow:visible; padding:0 }
        .rz table, .rz tbody{ display:block }
        .rz thead{ display:none }
        .rz tbody tr{ display:grid; grid-template-columns:repeat(4,1fr); gap:10px 8px; padding:14px 16px 16px; border-bottom:1px solid var(--rz-hair-2) }
        .rz tbody tr:last-child{ border-bottom:0 }
        .rz td{ display:block; padding:0; border:0; white-space:normal; text-align:left; min-width:0; border-radius:0 !important }
        .rz td.piece{ grid-column:1 / -1; max-width:none; font-size:14.5px; font-weight:600; line-height:1.35; overflow:visible; white-space:normal }
        .rz td.pilar{ grid-column:1 / 3 }
        .rz td.edad{ grid-column:3 / 5; text-align:right; font-size:12.5px; align-self:center }
        .rz td.edad::before{ content:"hace " }
        .rz td.m{ font-family:var(--rz-display); font-size:18px; font-weight:800; letter-spacing:-.02em }
        .rz td.e{ grid-column:1 / -1; margin-top:2px }
        .rz td[data-l]::before{ content:attr(data-l); display:block; font-size:10px; font-weight:600; letter-spacing:.07em; text-transform:uppercase; color:var(--rz-ink-4); margin-bottom:2px }
        .rz td.e .rz-eng{ justify-content:flex-start; gap:10px }
        .rz td.e .rz-eng b{ min-width:0; text-align:left; font-size:16px }
        .rz td.e .rz-mini{ flex:1; max-width:150px; height:6px }
      }
      @media (prefers-reduced-motion:reduce){ .rz *{ transition:none !important } }
    `}</style>
  )
}
